import { excludeFrom, sampleRandom } from '@gametailors/web-core/misc';
import { AnnotationUtils, TAnnotatedKlankvoet } from '../../utils/annotation.utils';
import { HOMOPHONES, WordToKlankConverter } from '../../utils/klank.utils';

/** Cycles a tile through wrong options, coming back to the correct one now and then. */
export class KlankGenerator {
  private seen: string[] = [];
  private current: string | undefined = undefined;

  public constructor(
    private readonly correct: string,
    private readonly candidates: string[],
  ) {}

  public static forKlank(word: string[], index: number): KlankGenerator {
    const exclude = index === word.length - 1 ? ['v', 'c', 'z'] : []; // avoid illegal letters at the end
    return new KlankGenerator(word[index], WordToKlankConverter.getKlankVariantsFor(word[index], Infinity, exclude));
  }

  public static forKlankvoet(voet: TAnnotatedKlankvoet): KlankGenerator {
    return new KlankGenerator(AnnotationUtils.write(voet), KlankvoetVariants.for(voet));
  }

  getNext(): string {
    const shown = this.current === undefined ? [] : [this.current];
    let pool = excludeFrom(this.candidates, [...this.seen, ...shown]);
    if (pool.length === 0) {
      this.seen = []; // every option has been shown: start over
      pool = excludeFrom(this.candidates, shown);
    }

    const chanceToGetCorrect = this.current === undefined ? 0 : 0.35;
    const showCorrect = this.current !== this.correct && (pool.length === 0 || this.seen.length >= 3 || Math.random() < chanceToGetCorrect);
    const next = showCorrect ? this.correct : (sampleRandom(pool, 1)[0] ?? this.correct);

    if (next !== this.correct) this.seen.push(next);
    this.current = next;
    return next;
  }
}

// Each spelling mapped to the spelling that stands for its sound, e.g. 'ij' -> 'ei'.
const SAME_SOUND: Record<string, string> = Object.fromEntries(
  HOMOPHONES.flatMap(([sound, ...spellings]) => spellings.map((spelling) => [spelling, sound])),
);
// A single klinker that closes a normale klankvoet is pronounced long: 'ta' sounds like 'taa'.
const OPEN_LONG: Record<string, string> = { a: 'aa', e: 'ee', o: 'oo', u: 'uu', i: 'ie' };
// A closing medeklinker loses its voice: 'hond' sounds like 'hont'.
const DEVOICED: Record<string, string> = { d: 't', b: 'p', z: 's', v: 'f' };

const CANNOT_START = ['ng', 'nk'];
const CANNOT_END = ['v', 'z', 'j', 'h', 'w'];

/** The wrong options for a klankvoet tile. */
export class KlankvoetVariants {
  /**
   * Readable alternatives for a klankvoet that each differ by one klank and never sound like the original,
   * since the child has to tell them apart by reading.
   */
  public static for(voet: TAnnotatedKlankvoet): string[] {
    const written = AnnotationUtils.write(voet);
    const sound = this.soundKey(voet);

    const variants = voet.klanken.flatMap((klank, i) =>
      this.canVary(voet, i)
        ? WordToKlankConverter.getKlankVariantsFor(AnnotationUtils.writeKlank(klank)).map((variant) => ({
            ...voet,
            klanken: voet.klanken.map((k, j) => (j === i ? variant : k)),
          }))
        : [],
    );

    const writtenVariants = variants.filter((v) => this.isReadable(v) && this.soundKey(v) !== sound).map((v) => AnnotationUtils.write(v));
    return [...new Set(writtenVariants)].filter((w) => w !== written);
  }

  /** How the klankvoet sounds, so that differently spelled but equal sounding klankvoeten compare equal. */
  public static soundKey(voet: TAnnotatedKlankvoet): string {
    const sounds = AnnotationUtils.writeKlanken(voet).map((klank) => SAME_SOUND[klank] ?? klank);
    const last = sounds.length - 1;
    if (!voet.stom) sounds[last] = OPEN_LONG[sounds[last]] ?? sounds[last];
    sounds[last] = DEVOICED[sounds[last]] ?? sounds[last];
    return sounds.join('/');
  }

  private static isMedeklinker(klank: string | undefined): boolean {
    return klank !== undefined && !AnnotationUtils.isKlinker(klank);
  }

  private static canVary(voet: TAnnotatedKlankvoet, i: number): boolean {
    const klank = voet.klanken[i];
    // keep dubbelzetters, langermaakregels and hyphens intact; a tekendief klinker may still change
    if (klank === '-' || klank.startsWith('+') || klank.startsWith('|')) return false;
    if (AnnotationUtils.isKlinker(klank)) return !voet.stom; // the sjwa of a stomme klankvoet stays

    // only a lone medeklinker: changing one inside a cluster ('str') quickly gives unreadable klankvoeten
    return !this.isMedeklinker(voet.klanken[i - 1]) && !this.isMedeklinker(voet.klanken[i + 1]);
  }

  private static isReadable(voet: TAnnotatedKlankvoet): boolean {
    const klanken = AnnotationUtils.writeKlanken(voet);
    return !CANNOT_START.includes(klanken[0]) && !CANNOT_END.includes(klanken[klanken.length - 1]);
  }
}
